'use client';

import React, { useEffect } from 'react';
import { Text, Box, SimpleGrid, Stack } from '@chakra-ui/react';
import { IBranch } from '@/_types';
import { useTranslations, useLocale } from 'next-intl';
import { useMemo } from 'react';
import { buildSections } from '@/features/brand-menu';
import type { ICategoryWithProducts } from '@/features/brand-menu';
import Card from '../components/Card';

interface BrandSingleMobileProps {
  data: IBranch | null;
  categories?: ICategoryWithProducts[];
}

const BrandSingleMobile: React.FC<BrandSingleMobileProps> = ({
  data,
  categories = [],
}) => {
  const t = useTranslations();
  const locale = useLocale();

  const sections = useMemo(
    () => buildSections(categories, locale),
    [categories, locale],
  );

  // Scroll to category when entering page with hash (e.g. /brand/yaponamama#category-slug)
  useEffect(() => {
    if (!sections.length) return;
    const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      const headerOffset = 80;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      const timeoutId = window.setTimeout(() => {
        window.scrollTo({ top: Math.max(0, offsetPosition), behavior: 'smooth' });
      }, 100);
      return () => window.clearTimeout(timeoutId);
    }
  }, [sections]);

  return (
    <Stack px={4} pt={4} pb={'100px'} gap={4}>
      {sections.length === 0 ? (
        <Box py={10} textAlign='center' color='text.muted'>
          {t('no_products')}
        </Box>
      ) : (
        sections.map((section) => (
          <Stack key={section.id} id={section.slug} gap={3}>
            <Text fontSize='xl' fontWeight={600}>
              {section.title}
            </Text>
            <SimpleGrid columns={{ base: 2, sm: 2 }} gap={{ base: 2, lg: 3 }}>
              {section.products.map((product) => (
                <Card
                  product={product}
                  key={`${section.id}-${product.id}-${product.image}`}
                />
              ))}
            </SimpleGrid>
          </Stack>
        ))
      )}
    </Stack>
  );
};

export default BrandSingleMobile;
